import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { DatePicker, validateSchedule } from "@platformx/utilities";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

type RescheduleDialogProps = {
  open: boolean;
  /** Which schedule is being moved. */
  kind: "publish" | "unpublish";
  /** The time currently scheduled (ISO-8601), shown until the editor picks a new one. */
  initialTime: string | null;
  onConfirm: (isoTime: string) => void;
  onClose: () => void;
};

/** A dayjs value from the picker as an ISO-8601 UTC string; null if it is not a valid date. */
const toIsoOrNull = (picked: any): string | null => {
  if (!picked) return null;
  const valid = typeof picked.isValid === "function" ? picked.isValid() : true;
  return valid ? picked.toISOString() : null;
};

/**
 * Moves a pending schedule publish/unpublish to a new time (KIWI-6576). Pages never had this dialog:
 * their reschedule menu items set state that nothing renders, so it is built here for content.
 *
 * It checks the new time against the same lead-time rule as everywhere else and disables Confirm
 * until the time is both valid and different. The publish-to-unpublish gap against the *other*
 * schedule is checked by `useContentSchedule`, which has that row's other trigger.
 */
export const RescheduleDialog = ({
  open,
  kind,
  initialTime,
  onConfirm,
  onClose,
}: RescheduleDialogProps) => {
  const { t } = useTranslation();
  const idBase = useId();
  const titleId = `${idBase}-title`;
  const errorId = `${idBase}-error`;
  const [time, setTime] = useState<string | null>(initialTime);

  // Re-opening for a different row must start from that row's time, not the last one edited.
  useEffect(() => {
    if (open) setTime(initialTime);
  }, [open, initialTime]);

  const verdict = validateSchedule({
    isSchedulePublish: kind === "publish",
    schedulePublishDateTime: kind === "publish" ? time : null,
    isScheduleUnpublish: kind === "unpublish",
    scheduleUnpublishDateTime: kind === "unpublish" ? time : null,
  });
  const changed = Boolean(time) && time !== initialTime;
  // No message until the editor has actually chosen something: the current time is, by definition,
  // already scheduled, so flagging it before they touch anything would be noise.
  const error = changed && !verdict.valid ? t(verdict.messageKey) : "";
  const canConfirm = changed && verdict.valid;
  const label = t(kind === "publish" ? "reschedule_publish" : "reschedule_unpublish");

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby={titleId} fullWidth maxWidth='xs'>
      <DialogTitle id={titleId}>{label}</DialogTitle>
      <DialogContent>
        <Box sx={{ paddingTop: "8px" }}>
          <DatePicker
            time={time}
            handleValChange={(picked) => setTime(toIsoOrNull(picked))}
            isDisabled={false}
            error={Boolean(error)}
            ariaLabel={label}
            ariaDescribedBy={error ? errorId : undefined}
          />
          {error ? (
            // Text, not just a red border: colour alone must not carry the error (WCAG 2.2 AA).
            <Box id={errorId} role='alert' sx={{ marginTop: "6px", fontSize: "13px" }}>
              {error}
            </Box>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant='secondaryButton' onClick={onClose} data-testid='reschedule-cancel'>
          {t("cancel")}
        </Button>
        <Button
          variant='primaryButton'
          disabled={!canConfirm}
          onClick={() => time && onConfirm(time)}
          data-testid='reschedule-confirm'>
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
